/**
 * Behavior for sections/swist-announcement.liquid.
 * Marquee: messages are rendered once; this adds hidden, inert copies until the strip is
 * wider than the bar, then scrolls exactly one copy's width per loop at a constant speed.
 * Rotate: shows one message at a time (the old one slides up, the next rises in) with prev/next. Both share one countdown timer that
 * hides finished countdowns (and their message when the merchant asked for it).
 */
if (!customElements.get('swist-announcement')) {
  customElements.define(
    'swist-announcement',
    class SwistAnnouncement extends HTMLElement {
      #abort = new AbortController();
      #countdownTimer = 0;
      #rotateTimer = 0;
      #frame = 0;
      #index = 0;
      #hovered = false;
      /** @type {ResizeObserver | undefined} */
      #resizeObserver;
      #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      connectedCallback() {
        const { signal } = this.#abort;
        this.list = this.querySelector('[data-sab-list]');
        this.belt = this.querySelector('[data-sab-belt]');

        this.querySelector('[data-sab-pause]')?.addEventListener('click', () => this.#togglePause(), { signal });
        this.querySelector('[data-sab-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
        this.querySelector('[data-sab-next]')?.addEventListener('click', () => this.#step(1), { signal });
        this.addEventListener('pointerenter', () => (this.#hovered = true), { signal });
        this.addEventListener('pointerleave', () => (this.#hovered = false), { signal });
        this.#reducedMotion.addEventListener('change', () => this.#layout(), { signal });

        this.#startCountdowns();
        this.#layout();
      }

      disconnectedCallback() {
        this.#abort.abort();
        this.#abort = new AbortController();
        clearInterval(this.#countdownTimer);
        clearInterval(this.#rotateTimer);
        cancelAnimationFrame(this.#frame);
        this.#resizeObserver?.disconnect();
      }

      get #isRotate() {
        return this.dataset.mode === 'rotate';
      }

      #layout() {
        if (this.#isRotate) {
          this.#setupRotate();
          return;
        }
        if (!this.#resizeObserver) {
          this.#resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(this.#frame);
            this.#frame = requestAnimationFrame(() => this.#fillMarquee());
          });
          this.#resizeObserver.observe(this);
        }
        this.#fillMarquee();
      }

      /* ── Marquee ── */

      #fillMarquee() {
        const { list, belt } = this;
        if (!(list instanceof HTMLElement) || !(belt instanceof HTMLElement)) return;

        for (const copy of belt.querySelectorAll('[data-sab-copy]')) copy.remove();

        const copyWidth = list.getBoundingClientRect().width;
        const pause = this.querySelector('[data-sab-pause]');
        if (!copyWidth || this.#reducedMotion.matches) {
          this.classList.remove('is-running');
          if (pause instanceof HTMLElement) pause.hidden = true;
          return;
        }

        // One extra copy beyond what fills the bar keeps the loop seamless
        const copies = Math.ceil(this.clientWidth / copyWidth) + 1;
        for (let i = 0; i < copies; i += 1) {
          const copy = /** @type {HTMLElement} */ (list.cloneNode(true));
          copy.removeAttribute('data-sab-list');
          copy.setAttribute('data-sab-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.inert = true;
          belt.append(copy);
        }

        const speed = Number(this.dataset.speed) || 45;
        const direction = getComputedStyle(this).direction === 'rtl' ? 1 : -1;
        belt.style.setProperty('--sab-shift', `${direction * copyWidth}px`);
        belt.style.setProperty('--sab-duration', `${(copyWidth / speed).toFixed(2)}s`);
        this.classList.add('is-running');
        if (pause instanceof HTMLElement) pause.hidden = false;
      }

      #togglePause() {
        const pause = this.querySelector('[data-sab-pause]');
        const paused = !this.classList.contains('is-paused');
        this.classList.toggle('is-paused', paused);
        if (!(pause instanceof HTMLElement)) return;
        pause.setAttribute('aria-pressed', String(paused));
        pause.setAttribute('aria-label', pause.dataset[paused ? 'labelPlay' : 'labelPause'] ?? '');
      }

      /* ── One at a time ── */

      #items() {
        return [...(this.list?.querySelectorAll('.sab__item:not([hidden])') ?? [])];
      }

      #setupRotate() {
        const items = this.#items();
        this.classList.add('is-ready');
        if (this.#index >= items.length) this.#index = 0;
        this.#show(this.#index);
        this.#schedule();
      }

      #show(index) {
        const items = this.#items();
        if (items.length === 0) return;
        const previous = this.list?.querySelector('.sab__item.is-active') ?? null;
        this.#index = (index + items.length) % items.length;
        items.forEach((item, position) => {
          const active = position === this.#index;
          // The outgoing message slides up while the next one rises in
          item.classList.toggle('is-leaving', !active && item === previous);
          item.classList.toggle('is-active', active);
          item.toggleAttribute('inert', !active);
          item.setAttribute('aria-hidden', String(!active));
        });
      }

      #schedule() {
        clearInterval(this.#rotateTimer);
        if (this.#items().length < 2 || this.#reducedMotion.matches) return;
        const seconds = Number(this.dataset.interval) || 5;
        this.#rotateTimer = window.setInterval(() => {
          if (this.#hovered || this.contains(document.activeElement) || document.hidden) return;
          this.#show(this.#index + 1);
        }, seconds * 1000);
      }

      #step(direction) {
        this.#show(this.#index + direction);
        this.#schedule();
      }

      /* ── Countdowns ── */

      #startCountdowns() {
        if (!this.querySelector('[data-sab-end]')) return;
        const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');

        const tick = () => {
          let changed = false;
          for (const countdown of this.querySelectorAll('[data-sab-end]')) {
            if (!(countdown instanceof HTMLElement) || countdown.hidden) continue;
            const end = Date.parse(countdown.dataset.sabEnd ?? '');
            if (Number.isNaN(end)) continue;

            const left = Math.max(0, end - Date.now());
            const parts = {
              d: Math.floor(left / 86400000),
              h: Math.floor((left % 86400000) / 3600000),
              m: Math.floor((left % 3600000) / 60000),
              s: Math.floor((left % 60000) / 1000),
            };
            // Write only digits that changed: each write is a DOM mutation that wakes page observers
            for (const [key, value] of Object.entries(parts)) {
              const node = countdown.querySelector(`[data-${key}]`);
              const text = key === 'd' ? String(value) : pad(value);
              if (node && node.textContent !== text) node.textContent = text;
            }
            const daysBox = countdown.querySelector('[data-d-wrap]');
            if (daysBox instanceof HTMLElement && daysBox.hidden !== (parts.d === 0)) daysBox.hidden = parts.d === 0;

            if (left > 0 || countdown.closest('[data-sab-copy]')) continue;
            countdown.hidden = true;
            changed = true;
            const item = countdown.closest('.sab__item');
            if (item instanceof HTMLElement && item.hasAttribute('data-hide-expired')) item.hidden = true;
          }

          // A finished countdown changes the strip's width (or removes a message): lay out again
          if (!changed) return;
          if (this.#items().length === 0) this.hidden = true;
          else this.#layout();
        };

        tick();
        this.#countdownTimer = window.setInterval(tick, 1000);
      }
    }
  );
}
